import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {t} from "i18next";
import {CardListItem} from "components/Card";
import {ObservableIcon} from "components/EntityIcon";
import {useDeliberateObservables} from "hooks/useDeliberateObservables";
import EntityIconLoader from "../../EntityIcon/EntityIconLoader/EntityIconLoader";
import {EntityTitle} from "../Components/EntityTitle";

const ObservablesListItem = ({
  tooltip,
  count,
  showEventsCount = true,
  showPivotMenu = true,
  minimal = false,
  entity,
  ...otherProps
}) => {
  const {type, value} = entity;

  /**
   * Memoize input to 'useDeliberateObservables' to limited re-rendering.
   * If type/value have not changed, memoized object should be fed to the hook.
   */
  const {disposition, status} = useDeliberateObservables(
    useMemo(() => ({type, value}), [type, value])
  );

  return (
    <CardListItem
      leftSide={
        status === "completed" ? (
          <ObservableIcon
            type={type}
            disposition={disposition}
            minimal={minimal}
          />
        ) : (
          <EntityIconLoader />
        )
      }
      title={
        <EntityTitle
          title={value}
          pivotValues={{type, value}}
          {...{
            status,
            tooltip,
            showPivotMenu,
            minimal
          }}
        />
      }
      rightSide={
        showEventsCount ? t("overview.cards.eventCount", {count}) : null
      }
      minimal={minimal}
      entity={entity}
      {...otherProps}
    />
  );
};

ObservablesListItem.propTypes = {
  /**
   * Data object for entity
   */
  entity: PropTypes.object,
  /**
   * Object with `title` and `subtitle` keys to show as tooltip.
   */
  tooltip: PropTypes.object,
  /**
   * If provided, an event count will be shown in the `rightSide` section.
   * This has no effect for if `minimal={true}`.
   */
  count: PropTypes.number,
  /**
   * Default: true.
   * Show or Hide and event count in `rightSide`.
   * Requires a valid `count` prop value.
   *
   */
  showEventsCount: PropTypes.bool,
  /**
   * Default: true.
   *
   * by default a `<PlusMoreTag/>` will be shown below the list if:
   * - `limit` props is defined.
   * - data.length > limit
   *
   * To disable this behavior set showEventsCount={false}
   */
  showPivotMenu: PropTypes.bool,
  /**
   * Default Value: false - Render normal view (Cards, Drawers, etc.)
   * true - Renders a more compact view used by Tables. note that some sections
   * like 'subtitle' and 'rightSide' event counts are never show in this mode.
   */
  minimal: PropTypes.bool,
  /**
   * Remaining Entity properties passed through to CardListItem
   */
  otherProps: PropTypes.object
};

export default React.memo(ObservablesListItem);
